import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SlugInputModal from './SlugInputModal';
import { nameToSlug } from '../../utils/portfolio/slugUtils';
import { trackPortfolioCreate } from '../../utils/analytics/userActions';
import { clearStoredResults } from '../../utils/portfolio/storageUtils';

const PortfolioGenerator = ({ 
  userId, 
  sessionId, 
  resumeData, 
  externalModalOpen, 
  onModalClose, 
  existingPortfolio: existingPortfolioProp 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [existingPortfolio, setExistingPortfolio] = useState(existingPortfolioProp || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (externalModalOpen !== undefined) {
      setIsModalOpen(externalModalOpen);
    }
  }, [externalModalOpen]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsLoading(false);
    if (onModalClose) {
      onModalClose();
    }
  };

  useEffect(() => {
    const checkExistingPortfolio = async () => {
      if (!userId || !sessionId) {
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('portfolios')
          .select('*')
          .eq('session_id', sessionId)
          .is('deleted_at', null)
          .maybeSingle();
          
        if (error) {
          return;
        }
        
        if (data) {
          setExistingPortfolio(data);
        } else {
          setExistingPortfolio(null);
        }
      } catch (error) {
        // 오류 발생 시 조용히 무시
      }
    };
    
    checkExistingPortfolio();
  }, [userId, sessionId]);

  if (!userId) {
    return null;
  }

  const preparePortfolioData = async (slug, profileImageFile, aiResults = null, selectedImageType = 'original', selectedBackgroundColor = '#ffffff', hasNewImage = false) => {
    let imageUrl = existingPortfolio?.profile_image_url || null;
    let originalImageUrl = existingPortfolio?.original_image_url || null;
    let aiEnhancementData = existingPortfolio?.ai_enhancement_data || null;

    // AI 결과가 있는 경우 원본과 개선된 이미지들을 모두 업로드
    if (aiResults && aiResults.originalFile) {
      // 원본 이미지 업로드
      const originalFilePath = `public/${userId}/original/${Date.now()}-${aiResults.originalFile.name}`;
      const { error: originalUploadError } = await supabase.storage
        .from('portfolios')
        .upload(originalFilePath, aiResults.originalFile, { upsert: true });
      
      if (!originalUploadError) {
        const { data: originalUrlData } = supabase.storage
          .from('portfolios')
          .getPublicUrl(originalFilePath);
        originalImageUrl = originalUrlData.publicUrl;
      }

      // AI 개선된 이미지들 업로드
      const aiUrls = {};
      
      if (aiResults.gfpganFile) {
        const gfpganPath = `public/${userId}/ai/gfpgan/${Date.now()}-gfpgan.jpg`;
        const { error: gfpganError } = await supabase.storage
          .from('portfolios')
          .upload(gfpganPath, aiResults.gfpganFile, { upsert: true });
        
        if (!gfpganError) {
          const { data: gfpganUrlData } = supabase.storage
            .from('portfolios')
            .getPublicUrl(gfpganPath);
          aiUrls.gfpgan_url = gfpganUrlData.publicUrl;
        }
      }

      if (aiResults.gfpganBackgroundRemoverFile) {
        const bgRemoverPath = `public/${userId}/ai/bg-remover/${Date.now()}-bg-remover.jpg`;
        const { error: bgRemoverError } = await supabase.storage
          .from('portfolios')
          .upload(bgRemoverPath, aiResults.gfpganBackgroundRemoverFile, { upsert: true });
        
        if (!bgRemoverError) {
          const { data: bgRemoverUrlData } = supabase.storage
            .from('portfolios')
            .getPublicUrl(bgRemoverPath);
          aiUrls.background_remover_url = bgRemoverUrlData.publicUrl;
        }
      }

      if (aiResults.gfpganBackgroundRemoverBlackWhiteFile) {
        const bwPath = `public/${userId}/ai/bw/${Date.now()}-bw.jpg`;
        const { error: bwError } = await supabase.storage
          .from('portfolios')
          .upload(bwPath, aiResults.gfpganBackgroundRemoverBlackWhiteFile, { upsert: true });
        
        if (!bwError) {
          const { data: bwUrlData } = supabase.storage
            .from('portfolios')
            .getPublicUrl(bwPath);
          aiUrls.black_white_url = bwUrlData.publicUrl;
        }
      }

      if (aiResults.finalFile) {
        const finalPath = `public/${userId}/ai/final/${Date.now()}-final.jpg`;
        const { error: finalError } = await supabase.storage
          .from('portfolios')
          .upload(finalPath, aiResults.finalFile, { upsert: true });
        
        if (!finalError) {
          const { data: finalUrlData } = supabase.storage
            .from('portfolios')
            .getPublicUrl(finalPath);
          aiUrls.final_url = finalUrlData.publicUrl;
        }
      }

      // AI 개선 데이터 저장
      aiEnhancementData = {
        ...aiUrls,
        selected_type: selectedImageType,
        selected_background_color: selectedBackgroundColor,
        created_at: new Date().toISOString()
      };
    }
    
    // 이미지 처리 로직
    if (hasNewImage) {
      if (profileImageFile) {
        const timestamp = Date.now();
        const sanitizedFileName = profileImageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = `public/${userId}/${timestamp}-${sanitizedFileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('portfolios')
          .upload(filePath, profileImageFile, { 
            upsert: true,
            contentType: profileImageFile.type || 'image/jpeg'
          });
          
        if (uploadError) {
          throw uploadError;
        }
        
        const { data: urlData } = supabase.storage
          .from('portfolios')
          .getPublicUrl(filePath);
        imageUrl = urlData.publicUrl;
        
        if (!aiResults) {
          originalImageUrl = imageUrl;
        }
      } else {
        imageUrl = null;
        originalImageUrl = null;
        aiEnhancementData = null;
      }
    } else if (profileImageFile && selectedImageType !== 'original') {
      const timestamp = Date.now();
      const sanitizedFileName = profileImageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `public/${userId}/${timestamp}-${sanitizedFileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('portfolios')
        .upload(filePath, profileImageFile, { 
          upsert: true,
          contentType: profileImageFile.type || 'image/jpeg'
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data: urlData } = supabase.storage
        .from('portfolios')
        .getPublicUrl(filePath);
      imageUrl = urlData.publicUrl;
    }
    
    return {
      user_id: userId,
      session_id: sessionId,
      slug: slug,
      resume_snapshot: resumeData,
      profile_image_url: imageUrl,
      original_image_url: originalImageUrl,
      ai_enhancement_data: hasNewImage ? (aiResults ? aiEnhancementData : null) : aiEnhancementData,
      deleted_at: null
    };
  };

  const handleCreateOrUpdatePortfolio = async (slug, profileImageFile, aiResults = null, selectedImageType = 'original', selectedBackgroundColor = '#ffffff', hasNewImage = false, isPublished = false) => {
    setIsLoading(true);
    const isEditMode = !!existingPortfolio;

    try {
      const { data: testData, error: testError } = await supabase
        .from('portfolios')
        .select('*')
        .limit(1);
        
      if (testError && testError.code === 'PGRST116') {
        alert('웹페이지 기능이 아직 설정되지 않았습니다. 관리자에게 문의해주세요.');
        return;
      }

      // 포트폴리오 데이터 준비
      const portfolioData = await preparePortfolioData(
        slug, 
        profileImageFile, 
        aiResults, 
        selectedImageType, 
        selectedBackgroundColor, 
        hasNewImage
      );

      // 편집 모드면 기존 설정 유지하되 공개 상태는 업데이트, 새로 생성이면 전달받은 공개 설정 사용
      if (isEditMode) {
        portfolioData.status = isPublished ? 'published' : 'draft';
        portfolioData.visibility = existingPortfolio.visibility || {};
      } else {
        portfolioData.status = isPublished ? 'published' : 'draft';
        portfolioData.visibility = {}; // 기본값: 모든 항목 표시
      }

      await savePortfolio(portfolioData, isEditMode);

    } catch (err) {
      console.error('포트폴리오 생성/수정 오류:', err);
      
      if (err.code === 'PGRST116') {
        alert('웹페이지 기능이 아직 설정되지 않았습니다. 관리자에게 문의해주세요.');
      } else if (err.message && err.message.includes('storage')) {
        alert('이미지 업로드 중 오류가 발생했습니다. 파일 크기나 형식을 확인해주세요.');
      } else {
        alert('웹페이지 처리 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
      }
      setIsLoading(false);
    }
  };



  const savePortfolio = async (portfolioData, isEditMode) => {
    try {
      // 먼저 해당 세션에 삭제된 포트폴리오가 있는지 확인
      const { data: existingDeleted, error: checkError } = await supabase
        .from('portfolios')
        .select('id')
        .eq('session_id', sessionId)
        .not('deleted_at', 'is', null)
        .maybeSingle();

      let result;
      if (existingDeleted) {
        // 삭제된 포트폴리오가 있는 경우 업데이트
        result = await supabase
          .from('portfolios')
          .update(portfolioData)
          .eq('id', existingDeleted.id)
          .select()
          .single();
      } else {
        // 기존 upsert 로직 사용
        result = await supabase
          .from('portfolios')
          .upsert(portfolioData, { onConflict: 'session_id' })
          .select()
          .single();
      }
      
      const { data, error } = result;
      if (error) throw error;
      
      setExistingPortfolio(data);
      
      // 포트폴리오 생성/수정 성공 후 localStorage 초기화
      if (portfolioData.ai_enhancement_data) {
        clearStoredResults(userId, sessionId);
      }
      
      // 포트폴리오 생성 추적 (새로 생성된 경우만)
      if (!isEditMode) {
        try {
          await trackPortfolioCreate({
            user_id: userId,
            session_id: sessionId,
            resume_id: resumeData?.id || sessionId,
            portfolio_id: data.id,
            theme: 'default',
            creation_method: 'ai'
          });
        } catch (trackError) {
          console.warn('⚠️ 포트폴리오 생성 추적 실패:', trackError);
        }
        
        navigate(`/portfolio/${data.slug}`);
      }
      
      if (onModalClose) {
        onModalClose(data.slug);
      }

    } catch (err) {
      console.error('포트폴리오 저장 오류:', err);
      alert('웹페이지 저장 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SlugInputModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleCreateOrUpdatePortfolio}
        defaultSlug={existingPortfolio?.slug || nameToSlug(resumeData?.name)}
        isLoading={isLoading}
        isEditMode={!!existingPortfolio}
        userId={userId}
        sessionId={sessionId}
        existingPortfolio={existingPortfolio}
      onDelete={() => {
          if (onModalClose) {
            onModalClose(null, true);
          }
        }}
      />
    </>
  );
};

export default PortfolioGenerator;